"use strict";
// The Account Hub shell: open/close, join the shared modal-open counter,
// a query-string-based history.pushState router, and the real Overview
// panel (Orders/Settings still render the infra placeholder — not built
// yet). Follows the same shape as assets/flix-cart.js: a thin element,
// one delegated `document` click listener, everything else driven by
// plain functions — not the registered-Custom-Elements-API style
// assets/flix-toast.js uses, since this is an interactive overlay like
// <flix-cart>, not a self-contained widget like the toast container.
//
// Deep links use a query string (?panel=orders&order=<id>) rather than a
// path segment (/pages/account/orders) — Shopify only routes the page's
// own fixed path (/pages/account); anything past that a hard refresh would
// send to Shopify's server, which has no route for it. A query string on
// the existing page is the one scheme that's both pushState-able and
// actually served correctly on a hard refresh or a shared link.
//
// The persistent nav rail/mobile-nav (sections/account-hub.liquid) is
// static Liquid markup, not rendered from here — per the UI Prompt
// Package's 4.12, "Overview" isn't itself a rail row (it's the screen you
// land on), but is kept as the rail's first item for a way back once
// you've navigated elsewhere in the Hub — a small, deliberate deviation
// from the spec's literal list, for basic usability.
(function () {
    var bound = false;
    var PANEL_PARAM = "panel";
    var ORDER_PARAM = "order";
    var DEFAULT_PANEL = "overview";
    var VALID_PANELS = [
        "overview",
        "orders",
        "downloads",
        "notifications",
        "settings",
        "profile",
        "email",
        "security",
        "connected",
        "marketing",
        "delete-account",
    ];
    var MOBILE_QUERY = "(max-width: 768px)";
    /** The canonical empty-state mark: stroke 1.6, drawn to sit inside the 72px circle. */
    var SIGNED_OUT_ICON = '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
    function root() {
        return document.getElementById("flix-account-hub-root");
    }
    function content(el) {
        return el.querySelector("[data-flix-hub-content]");
    }
    function panelFromLocation() {
        var params = new URLSearchParams(window.location.search);
        var panel = params.get(PANEL_PARAM);
        return panel && VALID_PANELS.indexOf(panel) !== -1 ? panel : DEFAULT_PANEL;
    }
    function setChromeMode(el) {
        var isMobile = window.matchMedia(MOBILE_QUERY).matches;
        el.setAttribute("data-flix-hub-mode", isMobile ? "mobile" : "desktop");
    }
    function panelLabel(el, panel) {
        var key = "i18nNav" + panel.charAt(0).toUpperCase() + panel.slice(1);
        return el.dataset[key] || panel;
    }
    /** Design System 5.8: the active rail/mobile-nav item gets a surface2 bg pill + accent icon color — driven by this data-attribute (CSS in account-hub.liquid), kept in sync on both chrome copies together, same as the sign-out row's armed state. Per 5.8 the active item "reflects current sub-screen even when nested" — Profile lives under Settings (4.22's IA), so the Settings rail item stays lit while viewing it. */
    function updateActiveNav(panel) {
        // Profile and Email both live under Settings in the IA, so the rail
        // keeps Settings lit while either is open.
        var railPanel = panel === "profile" || panel === "email" ? "settings" : panel;
        document.querySelectorAll("[data-flix-hub-nav]").forEach(function (navEl) {
            navEl.dataset.active = navEl.dataset.flixHubNav === railPanel ? "true" : "false";
        });
    }
    var PANEL_TITLE_ID = "flix-hub-panel-title";
    /**
     * Gives the open dialog an accessible name.
     *
     * The panel carries `aria-labelledby="flix-hub-panel-title"`, so exactly
     * one element in the document has to answer to that id — and it has to be
     * re-pointed on every render, because each screen draws its own heading
     * and the previous one has just been thrown away with the markup.
     *
     * Panels are not consistent about their heading element: some use an h2,
     * others a styled div. So the first real heading wins, and a screen with
     * none at all falls back to naming the dialog directly rather than
     * pointing at an id that does not exist — an aria-labelledby aiming at
     * nothing leaves the dialog anonymous, which is worse than no attribute.
     */
    function labelPanel(el, contentEl) {
        var panelEl = el.querySelector("[data-flix-hub-panel]");
        if (!panelEl)
            return;
        var previous = document.getElementById(PANEL_TITLE_ID);
        if (previous)
            previous.removeAttribute("id");
        var heading = contentEl.querySelector("[data-flix-hub-title], h1, h2, h3");
        if (heading && (heading.textContent || "").trim().length > 0) {
            heading.id = PANEL_TITLE_ID;
            panelEl.setAttribute("aria-labelledby", PANEL_TITLE_ID);
            panelEl.removeAttribute("aria-label");
            return;
        }
        panelEl.removeAttribute("aria-labelledby");
        panelEl.setAttribute("aria-label", panelLabel(el, panelFromLocation()));
    }
    async function renderCurrentPanel() {
        var el = root();
        if (!el)
            return;
        var contentEl = content(el);
        if (!contentEl)
            return;
        await renderCurrentPanelContent(el, contentEl);
        labelPanel(el, contentEl);
        // style-hover/style-focus in JS-injected markup is inert until
        // compiled — the page-load pass in flix-hover.js only ever saw the
        // Liquid-rendered shell. Idempotent (compiling strips the attribute),
        // so re-running over the whole content area after each render is safe.
        window.flixCompileHoverStyles(contentEl);
    }
    /**
     * Which sign-in screen the panel is on.
     *
     * This is overlay state, not navigation: nothing about the storefront
     * changes as it moves, and closing the Hub discards it. "welcome" is the
     * invitation someone lands on — the invitation, the email field and the
     * Google/Discord fast path, all one screen — and "code" is the one step
     * that still follows it, once an emailed code is on the way.
     */
    var signInScreen = "welcome";
    var signInEmail = "";
    var signInChallengeId = "";
    function resetSignIn() {
        signInScreen = "welcome";
        signInEmail = "";
        signInChallengeId = "";
    }
    function renderSignInScreen(el, contentEl, error) {
        if (signInScreen === "code") {
            window.FlixHubSignIn.renderCode(el, contentEl, signInChallengeId, signInEmail, error);
            return;
        }
        window.FlixHubSignIn.renderWelcome(el, contentEl, signInEmail, error);
    }
    /**
     * Whether the Hub is showing an account or an invitation to sign in.
     *
     * The rail is authenticated navigation — Overview, Orders, Settings and
     * Sign out each address a session — so it is gated on this rather than
     * merely sitting behind the signed-out state. "unknown" is the moment
     * between opening the Hub and learning the answer, and it hides the rail
     * too: showing navigation and then taking it away is worse than never
     * showing it.
     */
    function setAuthState(el, value) {
        el.dataset.flixHubAuth = value;
    }
    async function renderCurrentPanelContent(el, contentEl) {
        var _a, _b;
        updateActiveNav(panelFromLocation());
        contentEl.innerHTML = window.FlixHubRender.loadingState(el.dataset.i18nLoading || "Loading…");
        setAuthState(el, "unknown");
        var profile;
        try {
            profile = await window.FlixAuthClient.checkSession();
        }
        catch (err) {
            // An unanswered question is not a session.
            setAuthState(el, "out");
            contentEl.innerHTML = window.FlixHubRender.errorState({
                title: el.dataset.i18nErrorTitle || "Something went wrong",
                message: el.dataset.i18nErrorMessage || "Please try again.",
                retryLabel: el.dataset.i18nRetry || "Retry",
            });
            return;
        }
        if (!profile) {
            setAuthState(el, "out");
            // The sign-in entry point is the identity origin's own /auth/start —
            // the existing BFF flow (ADR 014), not a login screen built here.
            // Derived from the configured API base so it follows the environment
            // rather than hardcoding a host; without that config there is
            // nowhere to send anyone, so the CTA is omitted rather than broken.
            var identityBase = (_b = (_a = window.flixIdentityConfig) === null || _a === void 0 ? void 0 : _a.apiBaseUrl) !== null && _b !== void 0 ? _b : "";
            // The identity origin serves its own sign-in screens (ADR 016), so it
            // needs to know which language the shopper was already browsing in -
            // otherwise the flow would switch language mid-sign-in. Passed as the
            // standard OIDC ui_locales parameter; the storefront language is
            // already on <html lang>, so nothing new has to be plumbed for it.
            var uiLocale = document.documentElement.lang || "";
            var startUrl = identityBase.replace(/\/+$/, "") + "/auth/start";
            if (uiLocale) {
                startUrl += "?ui_locales=" + encodeURIComponent(uiLocale);
            }
            var signInAction = identityBase
                ? {
                    action: {
                        label: el.dataset.i18nSignIn || "Sign in",
                        href: startUrl,
                    },
                }
                : {};
            // The invitation, then the methods, then the code — all in this same
            // panel. `signInAction` stays as the no-JavaScript fallback: an
            // anchor to the identity origin's own screens, used only if this
            // module never loaded.
            if (window.FlixHubSignIn) {
                renderSignInScreen(el, contentEl);
                return;
            }
            contentEl.innerHTML = window.FlixHubRender.emptyState({
                title: el.dataset.i18nSignedOutTitle || "You are not signed in",
                message: el.dataset.i18nSignedOutMessage || "Sign in to view your account.",
                iconSvg: SIGNED_OUT_ICON,
                ...signInAction,
            });
            return;
        }
        setAuthState(el, "in");
        var panel = panelFromLocation();
        if (panel === "overview") {
            await window.FlixHubOverview.render(el, contentEl, profile);
            return;
        }
        if (panel === "orders") {
            await window.FlixHubOrders.render(el, contentEl);
            return;
        }
        if (panel === "downloads") {
            await window.FlixHubDownloads.render(el, contentEl);
            return;
        }
        if (panel === "notifications") {
            await window.FlixHubNotifications.render(el, contentEl, profile);
            return;
        }
        if (panel === "settings") {
            window.FlixHubSettings.render(el, contentEl);
            return;
        }
        if (panel === "email") {
            window.FlixHubEmail.render(el, contentEl, profile);
            return;
        }
        if (panel === "profile") {
            window.FlixHubProfile.render(el, contentEl, profile);
            return;
        }
        if (panel === "security") {
            await window.FlixHubSecurity.render(el, contentEl);
            return;
        }
        if (panel === "marketing") {
            await window.FlixHubMarketing.render(el, contentEl);
            return;
        }
        if (panel === "delete-account") {
            window.FlixHubDelete.render(el, contentEl);
            return;
        }
        if (panel === "connected") {
            window.FlixHubConnected.render(el, contentEl, profile);
            return;
        }
        contentEl.innerHTML = window.FlixHubRender.emptyState({
            title: panelLabel(el, panel),
            message: el.dataset.i18nPanelPlaceholder || "This screen is coming in a later phase.",
        });
    }
    function navigateToPanel(panel, orderId) {
        var url = new URL(window.location.href);
        url.searchParams.set(PANEL_PARAM, panel);
        if (orderId) {
            url.searchParams.set(ORDER_PARAM, orderId);
        }
        else {
            url.searchParams.delete(ORDER_PARAM);
        }
        window.history.pushState({ flixHub: true }, "", url.toString());
        renderCurrentPanel();
    }
    function open() {
        var el = root();
        if (!el)
            return;
        el.hidden = false;
        setChromeMode(el);
        document.body.style.overflow = "hidden";
        window.flixModalOpen();
        renderCurrentPanel();
    }
    function close() {
        var el = root();
        if (!el)
            return;
        el.hidden = true;
        document.body.style.overflow = "";
        window.flixModalClose();
        closeSignOutDialog();
        resetSignIn();
    }
    /**
     * The rail and the mobile stacked-list nav each render their own copy of
     * the sign-out row (only one is ever visible at a time — see the mode
     * CSS), so anything done to that row is done to both.
     */
    function eachSignOutButton(fn) {
        document.querySelectorAll("[data-flix-hub-signout]").forEach(fn);
    }
    function dialog() {
        var el = root();
        return el ? el.querySelector("[data-flix-hub-dialog]") : null;
    }
    /**
     * Confirming sign-out is a dialog over the Hub, not a row that rewrites
     * its own label.
     *
     * The row used to arm itself on the first click and act on the second,
     * which made the control say one thing and mean another, gave the reader
     * nothing to cancel deliberately, and expired on a timer they could not
     * see. The dialog states the question, offers the action and a way out,
     * and leaves the row alone.
     *
     * There is one action because there is one capability: ending this
     * device's session. "All devices" has no backend — SignOutUseCase is
     * documented as this-device-only with everywhere-sign-out as a later
     * phase — so offering it would be a button that does nothing.
     */
    function openSignOutDialog() {
        var el = dialog();
        if (!el)
            return;
        el.hidden = false;
        el.style.display = "flex";
        var confirm = el.querySelector("[data-flix-hub-dialog-confirm]");
        if (confirm)
            confirm.focus();
    }
    function closeSignOutDialog() {
        var el = dialog();
        if (!el)
            return;
        el.hidden = true;
        el.style.display = "";
        // Return focus to the control that opened it.
        var trigger = document.querySelector("[data-flix-hub-signout]:not([hidden])");
        if (trigger)
            trigger.focus();
    }
    function signOutDialogOpen() {
        var el = dialog();
        return el !== null && !el.hidden;
    }
    /**
     * Signing out without leaving the page.
     *
     * The overlay stays open and re-renders: the session is gone, so
     * renderCurrentPanel finds no account and shows the signed-out
     * invitation in the auth shell. Nothing reloads, and the storefront
     * underneath is exactly where it was.
     */
    async function confirmSignOut() {
        closeSignOutDialog();
        try {
            await window.FlixAuthClient.signOut();
        }
        catch (err) {
            // signOut clears local state even when the network call fails, so
            // the panel below still renders the truth.
        }
        resetSignIn();
        await renderCurrentPanel();
    }
    /** Moves between sign-in screens. Overlay state only — nothing navigates. */
    function goToSignInScreen(screen) {
        var el = root();
        var contentEl = el ? content(el) : null;
        if (!el || !contentEl)
            return;
        signInScreen = screen;
        renderSignInScreen(el, contentEl);
    }
    async function sendSignInCode(form) {
        var el = root();
        var contentEl = el ? content(el) : null;
        if (!el || !contentEl)
            return;
        var input = form.querySelector('input[name="email"]');
        var button = form.querySelector("[data-flix-signin-send]");
        var email = (input && input.value.trim()) || "";
        if (!email)
            return;
        signInEmail = email;
        window.FlixHubSignIn.busy(button, true);
        try {
            var started = await window.FlixAuthClient.startEmailSignIn(email);
            signInChallengeId = started.challengeId;
            signInScreen = "code";
            renderSignInScreen(el, contentEl);
        }
        catch (err) {
            window.FlixHubSignIn.busy(button, false);
            signInScreen = "welcome";
            renderSignInScreen(el, contentEl, window.FlixHubSignIn.messageFor(el, err));
        }
    }
    async function resendSignInCode() {
        var el = root();
        var contentEl = el ? content(el) : null;
        if (!el || !contentEl || !signInChallengeId)
            return;
        try {
            var resent = await window.FlixAuthClient.resendEmailSignIn(signInChallengeId);
            signInChallengeId = resent.challengeId;
            signInEmail = resent.email;
            renderSignInScreen(el, contentEl);
        }
        catch (err) {
            renderSignInScreen(el, contentEl, window.FlixHubSignIn.messageFor(el, err));
        }
    }
    /**
     * The moment the overlay becomes an account.
     *
     * On success the session already exists as cookies set on that response,
     * so there is nothing to navigate to — the panel simply re-renders as the
     * Hub, in place, over the same storefront page.
     */
    async function confirmSignInCode(form) {
        var el = root();
        var contentEl = el ? content(el) : null;
        if (!el || !contentEl)
            return;
        var digits = form.querySelectorAll(".flix-signin-digit");
        var code = "";
        digits.forEach(function (digit) {
            code += digit.value.trim();
        });
        if (code.length !== window.FlixHubSignIn.CODE_LENGTH)
            return;
        var button = form.querySelector("[data-flix-signin-confirm]");
        window.FlixHubSignIn.busy(button, true);
        try {
            await window.FlixAuthClient.verifyEmailSignIn(signInChallengeId, code);
            resetSignIn();
            await renderCurrentPanel();
        }
        catch (err) {
            window.FlixHubSignIn.busy(button, false);
            renderSignInScreen(el, contentEl, window.FlixHubSignIn.messageFor(el, err));
        }
    }
    /**
     * Spreads a run of digits across the boxes, starting at one of them.
     *
     * The six boxes are one field wearing six borders, so a code pasted into
     * any of them belongs to all of them. Starting at the box that received
     * the paste rather than always at the first means pasting two digits into
     * box five does the obvious thing too.
     *
     * If the paste completes the code, focus lands on the last box; otherwise
     * on the first box still empty — the one the person has to type into next.
     */
    function fillSignInCodeFrom(target, digits) {
        var _a;
        var boxes = Array.prototype.slice.call(document.querySelectorAll(".flix-signin-digit"));
        var start = boxes.indexOf(target);
        if (start < 0)
            return;
        for (var offset = 0; offset < digits.length && start + offset < boxes.length; offset += 1) {
            var box = boxes[start + offset];
            if (box)
                box.value = digits.charAt(offset);
        }
        var landing = boxes.find(function (box) {
            return box.value === "";
        });
        (_a = (landing !== null && landing !== void 0 ? landing : boxes[boxes.length - 1])) === null || _a === void 0 ? void 0 : _a.focus();
    }
    /**
     * Typing moves to the next box; backspace steps back out of an empty one.
     *
     * `wasAlreadyEmpty` is the difference between the two things backspace
     * can mean. On a box that holds a digit it is an edit, so the caret stays
     * where the person is looking. On one that is already empty it is a
     * request to go back — and because the caret moves inside the keydown,
     * the browser's own deletion then lands on the box it moved to, so a
     * single press removes a single digit. Six boxes, one field.
     */
    function onSignInDigit(target, wasAlreadyEmpty) {
        var _a, _b;
        var value = target.value.replace(/\D/g, "").slice(-1);
        target.value = value;
        var boxes = Array.prototype.slice.call(document.querySelectorAll(".flix-signin-digit"));
        var index = boxes.indexOf(target);
        if (value && index >= 0 && index < boxes.length - 1) {
            (_a = boxes[index + 1]) === null || _a === void 0 ? void 0 : _a.focus();
            return;
        }
        if (!value && wasAlreadyEmpty && index > 0)
            (_b = boxes[index - 1]) === null || _b === void 0 ? void 0 : _b.focus();
    }
    function onClick(e) {
        var target = e.target;
        if (!target)
            return;
        var openTrigger = target.closest("[data-flix-hub-open]");
        if (openTrigger) {
            e.preventDefault();
            open();
            return;
        }
        var el = root();
        var withinHub = el !== null && (el.contains(target) || target.closest("flix-account-hub") !== null);
        if (!withinHub)
            return;
        if (target.closest("[data-flix-hub-close]") || target.closest("[data-flix-hub-backdrop]")) {
            close();
            return;
        }
        if (target.closest("[data-flix-hub-retry]")) {
            renderCurrentPanel();
            return;
        }
        if (target.closest("[data-flix-hub-dialog-confirm]")) {
            void confirmSignOut();
            return;
        }
        // Cancel, and the dialog's own scrim, both dismiss it with no effect.
        if (target.closest("[data-flix-hub-dialog-cancel]") ||
            (signOutDialogOpen() &&
                target.closest("[data-flix-hub-dialog]") !== null &&
                target.closest("[data-flix-hub-dialog-panel]") === null)) {
            closeSignOutDialog();
            return;
        }
        // While the dialog is up it owns the Hub: nothing behind it responds.
        if (signOutDialogOpen())
            return;
        if (target.closest("[data-flix-hub-signout]")) {
            openSignOutDialog();
            return;
        }
        var revokeTrigger = target.closest("[data-flix-security-revoke]");
        if (revokeTrigger) {
            var el = root();
            var contentEl = el ? content(el) : null;
            var sessionId = revokeTrigger.dataset.flixSecurityRevoke;
            if (el && contentEl && sessionId) {
                void window.FlixHubSecurity.revoke(el, contentEl, sessionId);
            }
            return;
        }
        var marketingToggle = target.closest("[data-flix-marketing-toggle]");
        if (marketingToggle) {
            var marketingEl = root();
            if (marketingEl)
                void window.FlixHubMarketing.toggle(marketingEl, marketingToggle);
            return;
        }
        var deleteTrigger = target.closest("[data-flix-delete-action]");
        if (deleteTrigger) {
            var deleteEl = root();
            var deleteContent = deleteEl ? content(deleteEl) : null;
            var deleteAction = deleteTrigger.dataset.flixDeleteAction;
            if (deleteEl && deleteContent) {
                if (deleteAction === "start")
                    void window.FlixHubDelete.start(deleteEl, deleteContent);
                if (deleteAction === "confirm")
                    void window.FlixHubDelete.confirm(deleteEl, deleteContent);
            }
            return;
        }
        var removeMethodTrigger = target.closest("[data-flix-connected-remove]");
        if (removeMethodTrigger) {
            var connectedEl = root();
            var connectedContent = connectedEl ? content(connectedEl) : null;
            var authMethodId = removeMethodTrigger.dataset.flixConnectedRemove;
            if (connectedEl && connectedContent && authMethodId) {
                void window.FlixHubConnected.remove(connectedEl, connectedContent, authMethodId);
            }
            return;
        }
        var signInTrigger = target.closest("[data-flix-signin-action]");
        if (signInTrigger) {
            var action = signInTrigger.dataset.flixSigninAction;
            if (action === "resend") {
                void resendSignInCode();
            }
            else if (action === "welcome") {
                goToSignInScreen(action);
            }
            return;
        }
        var navTrigger = target.closest("[data-flix-hub-nav]");
        if (navTrigger instanceof HTMLElement) {
            var panel = navTrigger.dataset.flixHubNav;
            if (panel)
                navigateToPanel(panel, navTrigger.dataset.flixHubOrderId);
        }
    }
    function onPopState() {
        var el = root();
        if (el && !el.hidden) {
            renderCurrentPanel();
        }
    }
    function onResize() {
        var el = root();
        if (el && !el.hidden)
            setChromeMode(el);
    }
    function onSessionExpired() {
        close();
    }
    if (!bound) {
        bound = true;
        document.addEventListener("click", onClick);
        // The sign-in forms submit rather than being clicked, and the code boxes
        // move focus as they fill — both delegated like everything else here.
        document.addEventListener("submit", function (event) {
            var form = event.target;
            if (!(form instanceof HTMLFormElement))
                return;
            if (form.hasAttribute("data-flix-signin-email-form")) {
                event.preventDefault();
                void sendSignInCode(form);
                return;
            }
            if (form.hasAttribute("data-flix-signin-code-form")) {
                event.preventDefault();
                void confirmSignInCode(form);
            }
        });
        document.addEventListener("input", function (event) {
            var target = event.target;
            if (target instanceof HTMLInputElement && target.classList.contains("flix-signin-digit")) {
                onSignInDigit(target, false);
            }
        });
        /**
         * Escape, heard on `window` in the capture phase — the earliest point a
         * key can be seen, ahead of every listener on `document`.
         *
         * It used to be handled below with the rest of the keys, in the bubble
         * phase. The support chat widget stops Escape during capture while its
         * teaser is showing, so with the teaser up the first Escape dismissed
         * the teaser — invisible behind this dialog — and the Hub stayed open.
         * The Hub is the top layer whenever it is open, so it must hear the key
         * first. Propagation is left alone: the widget still dismisses its own
         * teaser on the same press.
         */
        window.addEventListener("keydown", function (event) {
            if (event.key !== "Escape")
                return;
            var hub = root();
            if (!hub || hub.hidden)
                return;
            if (signOutDialogOpen()) {
                closeSignOutDialog();
            }
            else {
                close();
            }
            event.preventDefault();
        }, true);
        document.addEventListener("keydown", function (event) {
            var target = event.target;
            /**
             * Escape dismisses the panel, the way a dialog is expected to.
             *
             * The Hub is `role="dialog" aria-modal="true"`, so a keyboard user has
             * every reason to expect Escape to work; until now the only way out
             * from the keyboard was to tab to the close button. It routes through
             * the SAME `close()` the close button uses, so there is one close path
             * and not two — body scroll, the modal counter, the sign-out dialog and
             * the sign-in state are all reset identically.
             *
             * Two guards. It does nothing unless the Hub is actually open, so
             * Escape anywhere else on the storefront — and any other dialog's own
             * Escape handling — is untouched. And when the sign-out confirmation is
             * up, Escape dismisses THAT and leaves the Hub open: the top layer goes
             * first, so the key never discards more context than was asked for.
             */
            // Handled on window, in capture — see the listener above.
            if (event.key === "Escape")
                return;
            if (event.key === "Backspace" &&
                target instanceof HTMLInputElement &&
                target.classList.contains("flix-signin-digit") &&
                target.value === "") {
                onSignInDigit(target, true);
            }
        });
        // Pasting the whole code fills the whole code.
        //
        // Every box is maxlength="1", so a pasted "013006" put a 0 in the box
        // under the cursor and dropped the other five — which is what everyone
        // does with a code they just copied out of an email.
        document.addEventListener("paste", function (event) {
            var _a, _b;
            var target = event.target;
            if (!(target instanceof HTMLInputElement))
                return;
            if (!target.classList.contains("flix-signin-digit"))
                return;
            var pasted = (_b = (_a = event.clipboardData) === null || _a === void 0 ? void 0 : _a.getData("text")) !== null && _b !== void 0 ? _b : "";
            var digits = pasted.replace(/\D/g, "");
            if (!digits)
                return;
            event.preventDefault();
            fillSignInCodeFrom(target, digits);
        });
        window.addEventListener("popstate", onPopState);
        window.addEventListener("resize", onResize);
        document.addEventListener("flix:auth:session-expired", onSessionExpired);
        // Coming back from Google or Discord.
        //
        // Their consent screens are top-level navigations, so the person does
        // leave the storefront — but they return to the exact page they left
        // (the identity origin was told where that was, and checked it against
        // its own allowlist), and the overlay reopens on it. Without this the
        // round trip silently loses the fact that they had opened Account at
        // all, which is the whole thing this flow is supposed to preserve.
        //
        // The marker is a flag, never a credential: the session arrived as
        // cookies this script cannot read. It is stripped immediately so the
        // URL is the one they started with, and so a reload does not reopen
        // an overlay they have since closed.
        function openFromReturn() {
            var url = new URL(window.location.href);
            if (url.searchParams.get("flix_account") !== "open")
                return;
            url.searchParams.delete("flix_account");
            window.history.replaceState(window.history.state, "", url.toString());
            open();
        }
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", openFromReturn);
        }
        else {
            openFromReturn();
        }
        // The dedicated /pages/account page is the Hub's home, not just a
        // trigger for it — it auto-opens there instead of waiting for a click.
        if (document.body.hasAttribute("data-flix-hub-auto-open")) {
            document.addEventListener("DOMContentLoaded", open);
        }
    }
})();
